import React from 'react';
import { Stack } from '@mui/material';

import { PointToPointLine } from '@paragon/contract-library';
import { DistanceField } from '@paragon/material';

import { findAndSetReferenceLine } from '../../../../store/actions/editorActions';
import { startSetReferenceLineIntersection } from '../../../../store/actions/insertActions';
import { getAsyncRequestForActiveComponentDesign } from '../../../../store/selectors/getAsyncRequestForActiveComponentDesignSelector';
import { getReferenceLineLongNamesByGuidForActiveComponent } from '../../../../store/selectors/getReferenceLineLongNamesByGuidForActiveComponentSelector';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import ReferenceLineIntersectionProperty from '../elements/ReferenceLineIntersectionProperty';
import ReferenceLineNameProperty from '../elements/ReferenceLineNameProperty';
import { ReferenceLineTypeProperty } from '../elements/ReferenceLineTypeProperty';

interface Props {
  line: PointToPointLine;
  afterUpdateLine?: () => void;
}

const PointToPointLineProperties = ({ line, afterUpdateLine }: Props) => {
  const dispatch = useAppDispatch();
  const referenceLineLongNamesByGuid = useAppSelector(getReferenceLineLongNamesByGuidForActiveComponent);
  const disabled = useAppSelector((state) => {
    const asyncRequest = getAsyncRequestForActiveComponentDesign(state);
    return propertiesShouldBeViewOnly(state) || (!!asyncRequest && asyncRequest.type !== 'updateFeatures');
  });

  const onApplyOffset = async (newOffset: number) => {
    const newLine = { ...line, offset: newOffset };
    await dispatch(findAndSetReferenceLine(newLine));
    afterUpdateLine && afterUpdateLine();
  };

  if (!referenceLineLongNamesByGuid) return null;

  return (
    <Stack direction="column" spacing={2}>
      <ReferenceLineNameProperty sx={{ marginBottom: '0!important' }} line={line} disabled={disabled} />
      <ReferenceLineTypeProperty line={line} disabled={disabled} />

      <ReferenceLineIntersectionProperty
        referenceLineLongNamesByGuid={referenceLineLongNamesByGuid}
        disabled={disabled}
        line={line}
        lineKeys={{ key1: 'basePointLine1Guid', key2: 'basePointLine2Guid' }}
        property="Base Point"
        onStartSelectingIntersection={(referenceLine, key1, key2) =>
          dispatch(startSetReferenceLineIntersection(referenceLine, key1, key2))
        }
      />

      <ReferenceLineIntersectionProperty
        referenceLineLongNamesByGuid={referenceLineLongNamesByGuid}
        disabled={disabled}
        line={line}
        lineKeys={{ key1: 'endPointLine1Guid', key2: 'endPointLine2Guid' }}
        property="End Point"
        onStartSelectingIntersection={(referenceLine, key1, key2) =>
          dispatch(startSetReferenceLineIntersection(referenceLine, key1, key2))
        }
      />

      <DistanceField value={line.offset} label="Offset" onSubmit={onApplyOffset} disabled={disabled} />
    </Stack>
  );
};

export default PointToPointLineProperties;
